import { clearsValues } from '../../attribute-filter-state';
import {
  ONE_DAY_FROM_NOW_SECONDS,
  isAbsoluteTimestampOperator,
  isRelativeTimestampOperator,
} from '../../relative-timestamp';

/**
 * The value a row should hold after an operator switch.
 *
 * Three outcomes, checked in this order:
 *
 * 1. **Seed.** Arriving at a relative-date operator from anything that isn't
 *    one, the row starts at one day from now rather than empty — a relative
 *    offset is signed seconds, so no other operator's value means anything
 *    here, and an empty relative row is immediately invalid. Absolute counts as
 *    "anything else": its value is an epoch, which read as an offset would be
 *    a nonsense span of decades.
 * 2. **Clear**, wherever the shared boundary rule says the value can't carry.
 * 3. **Keep.**
 *
 * The seed is checked before the clear rule rather than after, because the two
 * overlap: every transition that needs a seed is also one the boundary rule
 * would clear. Ordering them this way is what makes the seed reachable, and is
 * why this lives in one function instead of at each call site.
 */
export function valueForOperatorChange(
  nextOperator: string,
  prevOperator: string,
  value: string | null,
): string | null {
  if (isRelativeTimestampOperator(nextOperator)) {
    return isRelativeTimestampOperator(prevOperator) ? value : ONE_DAY_FROM_NOW_SECONDS;
  }
  // The other half of the variant switch: an offset can't be read as an epoch
  // either. `clearsValues` treats the whole inequality family as
  // value-compatible, which is right for after/before but not across variants —
  // a 3-day offset would land the picker in January 1970, and an offset above
  // the minimum epoch would pass validation and commit a wrong date.
  if (isAbsoluteTimestampOperator(nextOperator) && isRelativeTimestampOperator(prevOperator)) {
    return null;
  }
  return clearsValues(nextOperator, prevOperator) ? null : value;
}
